import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Check, ChevronDown, Search } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import type { FilterOption } from './filter-pills'

const normalize = (text: string) => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim()

export interface FilterMenuPanelProps {
  value: string
  onChange: (value: string) => void
  close: () => void
}

export interface FilterMenuProps<V extends string = string> {
  /** The facet's name ("Category"); with a value chosen the pill reads "Category: Jobs". */
  label: string
  value: V
  onChange: (value: V) => void
  options?: FilterOption<V>[]
  /** The value that means "no filter" (default ''). */
  defaultValue?: V
  /** A first item that goes back to the default ("All categories"). */
  anyLabel?: string
  /** A search box above the options (default: more than 10 options). */
  searchable?: boolean
  /** A panel of its own instead of an option list (two dates, a person picker). */
  render?: (props: FilterMenuPanelProps) => React.ReactNode
  /** What the active pill shows after the label, for a `render` facet. */
  displayValue?: string
  /** The pill names its value even at the default ("Período: Todo o período"), still without the active fill. */
  showValue?: boolean
  align?: 'start' | 'end'
  className?: string
  'data-testid'?: string
}

/** The pill of a menu facet: the same family as `FilterPills`, with a chevron. */
export const filterMenuTriggerClass = (active: boolean) =>
  cn(
    'inline-flex h-10 max-w-full items-center gap-1.5 whitespace-nowrap rounded-full px-3 text-[13px] font-medium transition-colors sm:h-9',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1',
    active ? 'bg-primary text-white' : 'bg-white text-gray-600 ring-1 ring-inset ring-gray-200 hover:bg-gray-50 hover:text-gray-900'
  )

/** The option list of a facet; also used inside the phone's filter sheet. */
export function FilterOptionList<V extends string = string>({ options, value, onPick, searchable, autoFocus }: {
  options: FilterOption<V>[]
  value: V
  onPick: (value: V) => void
  searchable?: boolean
  autoFocus?: boolean
}) {
  const { t } = useTranslation('filters')
  const [query, setQuery] = React.useState('')
  const listRef = React.useRef<HTMLDivElement>(null)
  const q = normalize(query)
  const shown = q ? options.filter((o) => normalize(o.label).includes(q)) : options

  /* Arrow keys move between the options, as in a menu. */
  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
    const items = Array.from(listRef.current?.querySelectorAll<HTMLButtonElement>('[data-filter-option]') ?? [])
    if (items.length === 0) return
    event.preventDefault()
    const at = items.indexOf(document.activeElement as HTMLButtonElement)
    const next = event.key === 'ArrowDown' ? (at + 1) % items.length : at <= 0 ? items.length - 1 : at - 1
    items[next]?.focus()
  }

  return (
    <div onKeyDown={onKeyDown}>
      {searchable && (
        <div className="relative mb-1 px-1 pt-1">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 mt-0.5 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden="true" />
          <input
            type="text"
            role="searchbox"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('searchOptions')}
            aria-label={t('searchOptions')}
            data-guard-ignore
            autoFocus={autoFocus}
            className="h-9 w-full rounded-md border border-gray-200 bg-white pl-8 pr-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary-200"
          />
        </div>
      )}
      <div ref={listRef} role="listbox" className="max-h-72 overflow-y-auto py-1">
        {shown.length === 0 ? (
          <p className="px-3 py-2 text-sm text-gray-500">{t('noOptions')}</p>
        ) : shown.map((option) => {
          const active = option.value === value
          const Icon = option.icon
          return (
            <button
              key={option.value || '__any'}
              type="button"
              role="option"
              aria-selected={active}
              data-filter-option
              data-value={option.value}
              onClick={() => onPick(option.value)}
              className="flex min-h-10 w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none sm:min-h-9"
            >
              <Check className={cn('h-4 w-4 flex-shrink-0 text-primary', active ? 'opacity-100' : 'opacity-0')} aria-hidden="true" />
              {Icon && <Icon className="h-4 w-4 flex-shrink-0 text-gray-400" aria-hidden="true" />}
              <span className="min-w-0 flex-1">
                <span className={cn('block truncate', active && 'font-medium text-gray-900')}>{option.label}</span>
                {option.description && <span className="block truncate text-xs text-gray-500">{option.description}</span>}
              </span>
              {option.count != null && <span className="text-xs tabular-nums text-gray-400">{option.count}</span>}
            </button>
          )
        })}
      </div>
    </div>
  )
}

/** The options of a facet with its "any" item first, when there is one. */
export const withAnyOption = <V extends string>(options: FilterOption<V>[], defaultValue: V, anyLabel?: string): FilterOption<V>[] =>
  anyLabel && !options.some((o) => o.value === defaultValue) ? [{ value: defaultValue, label: anyLabel }, ...options] : options

/**
 * A facet of a list (F-182, layer 2): a pill with a chevron that opens a menu,
 * for any cut that is not the main one, or the main one with more than 7
 * values. Shows the chosen value on the pill. Esc closes and gives the focus
 * back to the pill.
 */
export function FilterMenu<V extends string = string>({
  label, value, onChange, options = [], defaultValue = '' as V, anyLabel, searchable, render, displayValue, showValue, align = 'start', className, ...rest
}: FilterMenuProps<V>) {
  const [open, setOpen] = React.useState(false)
  const list = withAnyOption(options, defaultValue, anyLabel)
  const active = value !== defaultValue
  const chosen = active || showValue ? (displayValue ?? options.find((o) => o.value === value)?.label ?? (active ? value : null)) : null

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" aria-haspopup="dialog" className={cn(filterMenuTriggerClass(active), className)} data-testid={rest['data-testid']} data-active={active || undefined}>
          <span className="truncate">{chosen ? `${label}: ${chosen}` : label}</span>
          <ChevronDown className="h-4 w-4 flex-shrink-0 opacity-70" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent align={align} className={cn('p-1', render ? 'w-80' : 'w-64')} aria-label={label}>
        {render ? (
          <div className="p-2">{render({ value, onChange: (v) => onChange(v as V), close: () => setOpen(false) })}</div>
        ) : (
          <FilterOptionList
            options={list}
            value={value}
            searchable={searchable ?? options.length > 10}
            autoFocus
            onPick={(v) => { onChange(v); setOpen(false) }}
          />
        )}
      </PopoverContent>
    </Popover>
  )
}
